/**
 * Take the `#...` part off any URL on its way to Sentry.
 *
 * Plain words: when somebody clicks the reset link in their email, the proof that they did
 * is in the URL, after the `#`. It is a live access token and a refresh token, and it sits
 * in the address bar until the Supabase library has finished reading it, which is long
 * enough for our error monitoring to record it. Sending that to a third party would be
 * handing them the account. Sentry's `maskAllText` hides the password the user types; it
 * does nothing about URLs. So every URL Sentry is about to send loses its fragment first.
 * The path and the query survive, so a report still says which page broke (FR-011).
 *
 * This is a LEAF MODULE: it imports nothing, not even Sentry. That is what lets a test
 * import it without booting the app, and it keeps the rule it enforces readable on its own.
 */

/**
 * Everything after the first `#`, removed. Anything that is not a string comes back
 * untouched rather than throwing, because Sentry payloads are loosely typed and a missing
 * or odd value must never take down the reporting path.
 */
export function stripUrlFragment<T>(value: T): T {
  if (typeof value !== "string") return value;
  const hash = value.indexOf("#");
  return (hash === -1 ? value : value.slice(0, hash)) as T;
}

/** The loose shape of the part of a Sentry event that carries a URL. */
interface UrlBearingEvent {
  request?: { url?: string };
}

/** The loose shape of a Sentry breadcrumb. */
interface UrlBearingBreadcrumb {
  data?: Record<string, unknown>;
}

/**
 * Breadcrumb fields that hold a page URL. `from` and `to` are the navigation trail;
 * `url` is what fetch and history breadcrumbs use.
 */
const BREADCRUMB_URL_KEYS = ["from", "to", "url"] as const;

/** Clean the URL on an error event or a performance transaction, in place. */
export function scrubEventUrl(event: UrlBearingEvent): void {
  if (event.request?.url) {
    event.request.url = stripUrlFragment(event.request.url);
  }
}

/** Clean the URLs on a breadcrumb, in place. */
export function scrubBreadcrumbUrls(breadcrumb: UrlBearingBreadcrumb): void {
  const data = breadcrumb.data;
  if (!data) return;
  for (const key of BREADCRUMB_URL_KEYS) {
    if (typeof data[key] === "string") {
      data[key] = stripUrlFragment(data[key]);
    }
  }
}
